import { PrintedLanguage } from "./PrintedLanguage.ts";
import { ProblemData } from "./Constants.ts";

import TakeFromStartEn from "../Files/WordProblems/Types/en/takeFromStartUnknown.json";
import CompareBiggerEn from "../Files/WordProblems/Types/en/compareBiggerUnknown.json";
import AddToChangeEn from "../Files/WordProblems/Types/en/addToChangeUnknown.json";
import CompareDifferenceEn from "../Files/WordProblems/Types/en/compareDifferenceUnknown.json";
import PutTogetherEn from "../Files/WordProblems/Types/en/putTogetherPartUnknown.json";
import GroupsEn from "../Files/WordProblems/Types/en/groupsUnknown.json";
import ArrayEn from "../Files/WordProblems/Types/en/arraySideUnknown.json";
import TakeFromStartEs from "../Files/WordProblems/Types/es/takeFromStartUnknown.json";
import CompareBiggerEs from "../Files/WordProblems/Types/es/compareBiggerUnknown.json";
import AddToChangeEs from "../Files/WordProblems/Types/es/addToChangeUnknown.json";
import CompareDifferenceEs from "../Files/WordProblems/Types/es/compareDifferenceUnknown.json";
import PutTogetherEs from "../Files/WordProblems/Types/es/putTogetherPartUnknown.json";
import GroupsEs from "../Files/WordProblems/Types/es/groupsUnknown.json";
import ArrayEs from "../Files/WordProblems/Types/es/arraySideUnknown.json";
import TakeFromStartFr from "../Files/WordProblems/Types/fr/takeFromStartUnknown.json";
import CompareBiggerFr from "../Files/WordProblems/Types/fr/compareBiggerUnknown.json";
import AddToChangeFr from "../Files/WordProblems/Types/fr/addToChangeUnknown.json";
import CompareDifferenceFr from "../Files/WordProblems/Types/fr/compareDifferenceUnknown.json";
import PutTogetherFr from "../Files/WordProblems/Types/fr/putTogetherPartUnknown.json";
import GroupsFr from "../Files/WordProblems/Types/fr/groupsUnknown.json";
import ArrayFr from "../Files/WordProblems/Types/fr/arraySideUnknown.json";

/**
 * The seven story types of ADR 0073 §1: stories whose unknown is not the
 * result, or that compare, so a sheet that mixes operations has to be read
 * rather than matched on a keyword. What each type's numbers mean is the table
 * in Files/WordProblems/Types/README.md.
 *
 * Their own files and their own module, and `StoryTypes: Varied` the only way
 * in: adding a story to an existing pool would change what every seeded sheet
 * ever printed would say (ADR 0073 §2).
 */

export interface StoryTemplate {
    id?: number;
    story: string;
    question: string;
    problem: string;
}

export type StoryTypeId =
    | "takeFromStartUnknown"
    | "compareBiggerUnknown"
    | "addToChangeUnknown"
    | "compareDifferenceUnknown"
    | "putTogetherPartUnknown"
    | "groupsUnknown"
    | "arraySideUnknown";

/** The operators the new types serve, by the sign the arithmetic path gives a problem. */
export const STORY_TYPES_BY_OPERATOR: Record<string, StoryTypeId[]> = {
    "+": ["takeFromStartUnknown", "compareBiggerUnknown"],
    "-": ["addToChangeUnknown", "compareDifferenceUnknown", "putTogetherPartUnknown"],
    "÷": ["groupsUnknown", "arraySideUnknown"],
    // Multiplication gets no new type: the new division type is what breaks its cue (ADR 0073 §1).
    x: [],
};

export const ALL_STORY_TYPES: StoryTypeId[] = Object.values(STORY_TYPES_BY_OPERATOR).flat();

export const TYPE_STORIES: Record<PrintedLanguage, Record<StoryTypeId, StoryTemplate[]>> = {
    en: {
        takeFromStartUnknown: TakeFromStartEn,
        compareBiggerUnknown: CompareBiggerEn,
        addToChangeUnknown: AddToChangeEn,
        compareDifferenceUnknown: CompareDifferenceEn,
        putTogetherPartUnknown: PutTogetherEn,
        groupsUnknown: GroupsEn,
        arraySideUnknown: ArrayEn,
    },
    es: {
        takeFromStartUnknown: TakeFromStartEs,
        compareBiggerUnknown: CompareBiggerEs,
        addToChangeUnknown: AddToChangeEs,
        compareDifferenceUnknown: CompareDifferenceEs,
        putTogetherPartUnknown: PutTogetherEs,
        groupsUnknown: GroupsEs,
        arraySideUnknown: ArrayEs,
    },
    fr: {
        takeFromStartUnknown: TakeFromStartFr,
        compareBiggerUnknown: CompareBiggerFr,
        addToChangeUnknown: AddToChangeFr,
        compareDifferenceUnknown: CompareDifferenceFr,
        putTogetherPartUnknown: PutTogetherFr,
        groupsUnknown: GroupsFr,
        arraySideUnknown: ArrayFr,
    },
};

/**
 * Whether a problem can take one of the new stories. "Gave some more, and now
 * has 5" is false when the start was 5, and a take-from story about 0 left is
 * a puzzle, not a problem, so a 0 anywhere sends the problem back to its
 * operation's ordinary pool (ADR 0073 §3).
 */
export function canTakeVariedStory(problem: ProblemData): boolean {
    const [first, second, answer] = problem.numbers;
    return first > 0 && second > 0 && answer > 0;
}

/** The new types that serve an operator, in the order they rotate. */
export function storyTypesFor(operator: string): StoryTypeId[] {
    return STORY_TYPES_BY_OPERATOR[operator] ?? [];
}
